import _ from 'underscore';
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { Dropdown, MenuItem } from '@bingads-webui-react/primitive';
import { Toggle, TooltipHost, DirectionalHint } from '@fluentui/react';
import { useLocationsFromBingMapsApi } from '@bingads-webui-campaign-react/smart-component-bing-maps';
import { SmartTextEditor } from '@bingads-webui-campaign-react/smart-component-text';
import { useIsSmartPageMapEnabled, useEditorStatusContext } from '@bingads-webui-campaign-react/smart-page-context';
import { WEBSITE_SOURCE_DATA_KEY_MAPPING, } from '@bingads-webui-campaign-react/smart-component-util';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';

import { dataProperties } from '../data-config';
import { CustomizedToggle } from './customized-toggle';
import { CustomDropdownToggle } from './custom-dropdown-toggle';
import { formatGeoLocation } from './component-config-reader';

const getStyles = () => ({
  locationContainer: {
    '& .bing-maps-search-box-dropdown': {
      display: 'block',

      '& .bing-maps-search-box-results-menu': {
        width: '100%',
        marginTop: 0,
      },

      '& .bing-maps-search-box-results-menu.close': {
        display: 'none',
      },

      '& .bing-maps-search-box-search-result': {
        width: '100%',
        display: 'table',
        lineHeight: '20px',
        padding: '8px 0 8px 0',
      },

      '& .dropdown-menu > div > li > a': {
        height: 'auto',
        whiteSpace: 'normal',
        wordBreak: 'break-word',
      },
    },
  },
  mapToggleContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  warningIcon: {
    fontSize: '16px',
    marginTop: '3px',
    color: '#F1AF01',
  },
});

const toggleStyle = {
  marginTop: '8px',
  marginRight: '15px',
};

const UnstyledLocationWithMap = ({
  classes,
  i18n,
  errorMessage,
  isToggleDisabled,
  showLocation,
  showMap,
  locationInfo,
  businessAddressLabelId,
  activeDataProperty,
  dispatchSiteConfig,
  onMapToggle,
  onLocationToggle,
}) => {
  const [showMapWarning, setShowMapWarning] = useState(false);
  const [suggestResults, setSuggestResults] = useState([]);
  const [isListOpen, setIsListOpen] = useState(false);
  // If a dropdown list item is focused
  const [hasFocusElement, setHasFocusElement] = useState(false);

  const [, dispatchEditorStatus] = useEditorStatusContext();
  const isSmartPageMapEnabled = useIsSmartPageMapEnabled();
  const {
    getLocations: getLocationsFromBingMapApi,
    getAutoSuggestedLocations: getSuggestionsFromBingMapApi,
  } = useLocationsFromBingMapsApi();

  // Bing Map is availble under these conditions:
  // 1. Pilot is on.
  // 2. ShowLocation flag is on.
  // 3. ShowMap flag is not explicitily false value.
  const isBingMapEnabled = isSmartPageMapEnabled && showLocation && showMap !== false;

  // Get latitude and longitude from Bing map api service for a specific query
  const getGeoLocation = useCallback(_.debounce((address) => {
    getLocationsFromBingMapApi(address).then(({ geoLocation, isFallbackLoc }) => {
      dispatchSiteConfig({
        type: 'UPDATE_WEBSITESOURCEDATA_BY_NAME',
        payload: {
          name: WEBSITE_SOURCE_DATA_KEY_MAPPING.Locations,
          value: [formatGeoLocation({ ...geoLocation, isFallbackLoc }, true)],
        },
      });

      // Try to fetch a location but get a fallback result
      if (!_.isEmpty(address) && isFallbackLoc) {
        setShowMapWarning(true);
      } else {
        setShowMapWarning(false);
      }
    });
  }, 500), [dispatchSiteConfig, getLocationsFromBingMapApi]);

  // Get suggested queries from Bing map service when user types in location
  const getSuggestedLocations = useCallback(_.debounce(
    (query) => {
      getSuggestionsFromBingMapApi(query).then((results) => {
        setSuggestResults(results);
      });
    },
    500
  ), [getSuggestionsFromBingMapApi]);

  const onLocationChange = useCallback(({ value }) => {
    // If the user never used the map, when enters address, open it.
    if (_.isUndefined(showMap)) {
      onMapToggle({}, true);
    }

    getGeoLocation(value);
    getSuggestedLocations(value);
    // Change open status in round of event loop in case of errorMessage conflicts with dropdown
    setTimeout(() => {
      setIsListOpen(!_.isEmpty(value));
    });
  }, [getSuggestedLocations, getGeoLocation, onMapToggle, showMap]);

  // completeAddress = name + formattedAddress
  const onSelectSuggestedLocation = useCallback((completeAddress, formattedAddress, rawAddress) => {
    dispatchSiteConfig({
      type: 'UPDATE_DATASOURCE_BY_NAME',
      payload: {
        name: dataProperties.locationInfo,
        value: formattedAddress,
      },
    });

    dispatchEditorStatus({
      type: 'PATCH_BUFFER_STATE',
      payload: { rawAddress },
    });

    // Use shorter address to do the suggestions, completeAddress may affect the suggestion results.
    getSuggestedLocations(formattedAddress);
    getGeoLocation(completeAddress);
    setIsListOpen(false);
  }, [dispatchSiteConfig, getGeoLocation, getSuggestedLocations, dispatchEditorStatus]);

  const onInputFocus = useCallback(() => {
    if (!_.isEmpty(locationInfo) && showMap && isSmartPageMapEnabled) {
      setIsListOpen(true);
    }
    setHasFocusElement(false);
  }, [locationInfo, showMap, isSmartPageMapEnabled]);

  const onClickOutSide = useCallback((e) => {
    const dropDownList = document.getElementById('bing-maps-search-box-results-menu');
    const input = document.querySelector('.bing-maps-search-box-dropdown .ms-TextField-fieldGroup');
    const preview = document.querySelector(".contact-view div[data-property='locationInfo']");

    if (dropDownList && input && preview
      && !dropDownList.contains(e.target) && !input.contains(e.target)
      && !preview.contains(e.target)) {
      setIsListOpen(false);
    }
  }, []);

  const onKeyDown = useCallback((event) => {
    if (!hasFocusElement && isListOpen) {
      if (event.key === 'ArrowDown') {
        const aElement = document.querySelector('#bing-maps-search-box-results-menu > div > li > a');

        if (aElement && _.isFunction(aElement.focus)) {
          event.preventDefault();
          aElement.focus();
          setHasFocusElement(true);
        }
      }
    }
  }, [hasFocusElement, isListOpen]);

  useEffect(() => {
    if (!isListOpen) {
      setHasFocusElement(false);
    }
  }, [isListOpen]);

  useEffect(() => {
    if (!showMap) {
      setShowMapWarning(false);
    } else if (showMap && !_.isEmpty(locationInfo)) {
      getGeoLocation(locationInfo);
      getSuggestedLocations(locationInfo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showMap]);

  useEffect(() => {
    document.addEventListener('click', onClickOutSide);

    return () => {
      document.removeEventListener('click', onClickOutSide);

      dispatchEditorStatus({
        type: 'PATCH_BUFFER_STATE',
        payload: { rawAddress: null },
      });
    };
  }, []);

  const suggestedLocationMenu = useMemo(() => (
    _.isEmpty(suggestResults)
      ? (
        <MenuItem disabled>
          <div className="no-results">{i18n.getString(_TL_('No available address'))}</div>
        </MenuItem>
      )
      : _.map(suggestResults, ({
        key,
        name,
        completeAddress,
        formattedAddress,
        rawAddress,
      }) => (
        <MenuItem
          key={key}
          onSelect={() => onSelectSuggestedLocation(completeAddress, formattedAddress, rawAddress)}
        >
          <div className="bing-maps-search-box-search-result">
            <span>{`${formattedAddress}${name ? ` (${name})` : ''}`}</span>
          </div>
        </MenuItem>
      ))
  ), [i18n, onSelectSuggestedLocation, suggestResults]);

  return (
    <div className={classes.locationContainer}>
      <Dropdown
        id="bing-maps-search-box-dropdown"
        className="bing-maps-search-box-dropdown"
        open={isListOpen}
        onToggle={_.noop}
      >
        <CustomDropdownToggle bsRole="toggle">
          <SmartTextEditor
            onKeyDown={onKeyDown}
            label={i18n.getString(_TL_('Business address'))}
            onRenderLabel={({ label }) => (
              <CustomizedToggle
                i18n={i18n}
                onToggle={onLocationToggle}
                defaultChecked={showLocation}
                disabled={isToggleDisabled}
                label={label}
                id={businessAddressLabelId}
              />
            )}
            dataSourceName="locationInfo"
            focus={activeDataProperty === dataProperties.locationInfo}
            errorMessage={errorMessage}
            disabled={!showLocation}
            autoComplete="off"
            aria-labelledby={businessAddressLabelId}
            {...(
              isBingMapEnabled
                ? {
                  onChange: onLocationChange,
                  onFocus: onInputFocus,
                }
                : {}
            )
            }
          />
        </CustomDropdownToggle>
        <Dropdown.Menu
          role="menu"
          className={classNames('bing-maps-search-box-results-menu', isListOpen ? 'open' : 'close')}
          id="bing-maps-search-box-results-menu"
          data-clarity-unmask="true"
        >
          <div role="status">
            {suggestedLocationMenu}
          </div>
        </Dropdown.Menu>
      </Dropdown>
      {
        isSmartPageMapEnabled && (
          <div className={classes.mapToggleContainer}>
            <Toggle
              className="bing-map-toggle"
              inlineLabel
              label={i18n.getString(_TL_('Show map'))}
              disabled={!showLocation}
              checked={!!showMap}
              onChange={onMapToggle}
              styles={{ root: toggleStyle }}
            />
            {
              showMapWarning && showMap &&
              <TooltipHost
                content={i18n.getString(_TL_('We can\'t find your business address on Bing Maps.'))}
                directionalHint={DirectionalHint.bottomCenter}
              >
                <span
                  className={classNames(classes.warningIcon, 'iconba', 'iconba-IncidentTriangle')}
                />
              </TooltipHost>
            }
          </div>
        )
      }
    </div>
  );
};

UnstyledLocationWithMap.propTypes = {
  classes: PropTypes.shape({
    locationContainer: PropTypes.string.isRequired,
    mapToggleContainer: PropTypes.string.isRequired,
    warningIcon: PropTypes.string.isRequired,
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  errorMessage: PropTypes.string,
  isToggleDisabled: PropTypes.bool.isRequired,
  showLocation: PropTypes.bool.isRequired,
  showMap: PropTypes.bool,
  businessAddressLabelId: PropTypes.string.isRequired,
  activeDataProperty: PropTypes.string,
  dispatchSiteConfig: PropTypes.func.isRequired,
  onMapToggle: PropTypes.func.isRequired,
  onLocationToggle: PropTypes.func.isRequired,
  locationInfo: PropTypes.string.isRequired,
};

UnstyledLocationWithMap.defaultProps = {
  errorMessage: '',
  showMap: undefined,
  activeDataProperty: undefined,
};

UnstyledLocationWithMap.displayName = 'LocationWithMap';

export const LocationWithMap = withDefaultStyles(
  UnstyledLocationWithMap,
  getStyles
);
